/**
 * This is a utility function to create numeric colour scales.
 * It abstracts common logic for both continuous and heatmap scales,
 * such as range calculation, legend generation, and colour interpolation.
 * The specific interpolation logic is passed in via the options parameter,
 * allowing for flexible scale creation while keeping the core logic DRY.
 */

import { calculateValueRange } from '~utils/range';
import type { ColourLegendItem, ColourScale } from './types';

/** Options for creating a numeric colour scale */
type NumericScaleOptions = {
  /** Number of legend steps, indicates how many scale steps to generate */
  steps: number;
  /** Label formatter function to convert numeric values to display strings */
  formatLabel: (value: number) => string;
  /** Colour interpolation function - the "plug-in" that defines how values map to colours */
  interpolate: (value: number, min: number, max: number) => string;
};

/**
 * Factory function to create numeric (continuous) colour scales.
 * Handles common logic: range calculation, validation, legend generation.
 *
 * @internal - This is the shared method used by:
 * - `createContinuousScale` (OKLCH monochromatic gradients) in continuous.ts
 * - `createHeatmapScale` (jet-style multi-hue gradients) in heatmap.ts
 *
 * @template T - The type of data items
 * @param {T[]} data - Array of data items to base the scale on
 * @param {(keyof T & string)} property - Numeric property to base the scale on
 * @param {NumericScaleOptions} options - Scale configuration options
 * @return {Extract<ColourScale, { type: 'continuous' }>} A continuous colour scale with specified interpolation
 *
 * @example
 * ```ts
 * // In continuous.ts
 * createNumericScale(drillHoles, 'depth', {
 *   steps: 6,
 *   formatLabel: (v) => v.toFixed(2),
 *   interpolate: (value, min, max) => interpolateOklch(value, min, max, baseColour),
 * });
 *
 * // In heatmap.ts
 * createNumericScale(blocks, 'density', {
 *   steps: 5,
 *   formatLabel: (v) => v.toFixed(3),
 *   interpolate: interpolateHeatmap,  // Just pass the function directly
 * });
 * ```
 */
function createNumericScale<T extends Record<string, unknown>>(
  data: T[],
  property: keyof T & string,
  options: NumericScaleOptions,
): Extract<ColourScale, { type: 'continuous' }> {
  const { steps, formatLabel, interpolate } = options;

  // --- Helper: Builds the final scale object ---
  // Extracted to avoid repeating the same object structure in multiple return paths
  const buildScale = (
    min: number,
    max: number,
    legend: ColourLegendItem<number, string>[],
  ): Extract<ColourScale, { type: 'continuous' }> => ({
    type: 'continuous',
    property,
    min,
    max,
    legend,
    getColour: (value: number) => interpolate(value, min, max),
  });

  // --- Edge case: No data ---
  // Return a valid but empty scale to avoid null checks in consumers
  if (data.length === 0) return buildScale(0, 1, []);

  // --- Calculate value range ---
  // Extract min/max from the specified property across all data items
  const { min, max } = calculateValueRange(data, (item) => {
    const value = item[property];
    if (value === undefined || value === null) return NaN; // Will be filtered out by calculateValueRange
    if (typeof value !== 'number') throw new Error(`Property "${property}" must be a number, got ${typeof value}`);

    return value;
  });

  // --- Edge case: All values identical ---
  // When min equals max, interpolation would divide by zero
  // Return a single-item legend representing the uniform value
  if (min === max) {
    return buildScale(min, max, [{ value: min, colour: interpolate(min, min, max), label: formatLabel(min) }]);
  }

  // --- Generate legend items ---
  // Create evenly-spaced steps from min to max for the UI legend
  const step = (max - min) / (steps - 1);
  const allSteps = Array.from({ length: steps }, (_, i) => {
    const value = min + step * i;
    return { value, colour: interpolate(value, min, max), label: formatLabel(value) };
  });

  // Deduplicate entries that share the same label. This can happen when the data range
  // is narrower than the label precision (e.g. 6 steps between 1.0–2.0 with toFixed(0)
  // produces "1","1","1","2","2","2"). Keep the first occurrence of each label so the
  // displayed colour corresponds to the lowest value in that group.
  const seen = new Set<string>();
  const legend = allSteps.filter(({ label }) => {
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });

  return buildScale(min, max, legend);
}

export { createNumericScale, type NumericScaleOptions };
